import React, { SyntheticEvent, useState } from 'react';
import { AutoComplete, Button, Col, Input, Tag, Tooltip } from 'antd';
import { datasetFileStatusOptions } from '../../lib/status-templates';
import { SectionTitle, SectionSubTitle } from '../../components/text/Titles';
import { SectionRow } from '../../components/Section';
import { Icon } from '@ant-design/compatible';
import useGlobal from '../../store/Store';

type Props = {
  onPropertyChange?: (properties: {}) => any | null;
};

export const PropertySettings = ({ onPropertyChange }: Props) => {
  let s = '';

  const [globalState, globalActions] = useGlobal();

  if (globalState.properties) s = globalState.properties.tag;
  const [tag, setTag] = useState<string>(s);

  const onTagChange = (value: SyntheticEvent<HTMLInputElement>) => {
    setTag(value.currentTarget.value);
  };

  const saveTag = (value: string) => {
    globalActions.setTagEdit(false);
    let p = globalState.properties;
    if (p && p.tag === value) return;
    if (p && onPropertyChange) {
      p.tag = value;
      p.annotationState = 'ONGOING';
      onPropertyChange(p);
      if (!globalState.tags.includes(value)) {
        let t = globalState.tags;
        t.push(value);
        globalActions.setTags(t);
      }
    }
  };

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') saveTag(tag);
  };

  const setState = (state: string) => {
    let p = globalState.properties;
    if (p && onPropertyChange) {
      p.annotationState = state;
      onPropertyChange(p);
    }
  };

  const autocompleteoptions = {
    value: tag,
    dataSource: globalState.tags.filter(tag => typeof tag === 'string' && tag.length > 0),
    onChange: (value: any) => {
      setTag(value);
    },
    onSelect: (value: any) => {
      setTag(value);
      saveTag(value);
    },
    filterOption: (inputValue: any, option: any) => {
      return option.props.children.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;
    },
  };

  if (globalState.properties) {
    return (
      <>
        <SectionTitle fontSize={20}>Properties</SectionTitle>
        <SectionRow className="items-center">
          <Col span={4}>
            <SectionSubTitle>Tag</SectionSubTitle>
          </Col>
          <Col span={18} className="flex justify-between items-center">
            {globalState.tagEdit ? (
              <>
                <AutoComplete {...autocompleteoptions}>
                  <Input style={{ width: '200px' }} onKeyDown={handleKeyDown} onChange={onTagChange} value={tag} />
                </AutoComplete>
                <Button
                  onClick={() => {
                    globalActions.setTagEdit(false);
                  }}>
                  <Icon type="close" />
                </Button>
              </>
            ) : (
              <>
                <Tooltip title={globalState.properties.tag === '' ? 'Unlabeled' : globalState.properties.tag}>
                  <Tag style={{ width: '200px', overflowX: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
                    {globalState.properties.tag === '' ? 'Unlabeled' : globalState.properties.tag}
                  </Tag>
                </Tooltip>
                <Button
                  onClick={() => {
                    if (globalState.properties) setTag(globalState.properties.tag);
                    globalActions.setTagEdit(true);
                  }}>
                  <Icon type="edit" />
                </Button>
              </>
            )}
          </Col>
        </SectionRow>
        <SectionRow className="items-center">
          <Col span={10}>
            <SectionSubTitle>Annotation State</SectionSubTitle>
          </Col>
          <Col span={12} className="flex justify-between items-center">
            {globalState.properties.annotationState === 'SAVED' ? (
              <Tag style={{ width: '180px', textAlign: 'center' }}>Unchanged since commit</Tag>
            ) : (
              <Tag
                color={datasetFileStatusOptions[globalState.properties.annotationState]?.color}
                style={{ width: '120px', textAlign: 'center' }}>
                {datasetFileStatusOptions[globalState.properties.annotationState]?.label}
              </Tag>
            )}
            {globalState.properties.annotationState === 'DONE' && (
              <Button
                onClick={() => {
                  setState('ONGOING');
                }}>
                <Icon type="undo" />
              </Button>
            )}
            {['NEW', 'ONGOING'].includes(globalState.properties.annotationState) && (
              <Button
                onClick={() => {
                  setState('DONE');
                }}>
                <Icon type="check" />
              </Button>
            )}
          </Col>
        </SectionRow>
      </>
    );
  } else {
    return null;
  }
};
